import { useEffect, useState, createContext, useContext } from "react";
import { api } from "@/lib/api";

import { useTranslation } from "react-i18next";
/**
 * <EnvProvider> — fetches /api/dev/env once on mount and provides it to the
 * whole tree via `useEnv()`. Also caches to localStorage so the banner shows
 * instantly on subsequent navigations without a flash.
 */
const EnvCtx = createContext(null);

export function EnvProvider({ children }) {
  const { t } = useTranslation();
  const [envInfo, setEnvInfo] = useState(() => {
    try {
      const cached = localStorage.getItem("hisabx.env");
      if (cached) return JSON.parse(cached);
    } catch { /* ignore */ }
    // Best-effort synchronous guess so the banner doesn't flash on prod refresh
    const host = typeof window !== "undefined" ? window.location.hostname : "";
    const isPreview =
      host.includes("preview.emergentagent.com") ||
      host.includes("preview.emergentcf.cloud") ||
      host === "localhost" || host === "127.0.0.1";
    return { env: isPreview ? "preview" : "production", is_preview: isPreview, host, demo_credentials: [] };
  });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dev/env");
        if (cancelled) return;
        setEnvInfo(data);
        try { localStorage.setItem("hisabx.env", JSON.stringify(data)); } catch { /* ignore */ }
      } catch { /* the sync guess above is fine */ }
    })();
    return () => { cancelled = true; };
  }, []);
  return <EnvCtx.Provider value={envInfo}>{children}</EnvCtx.Provider>;
}

export function useEnv() {
  return useContext(EnvCtx) || { env: "production", is_preview: false, demo_credentials: [] };
}

/**
 * <EnvBanner> — top-of-page strip.
 *   - PREVIEW: prominent amber "safe to break" ribbon
 *   - PRODUCTION: minimal red-dot pill in the top-right, nothing more
 */
export function EnvBanner() {
  const { t } = useTranslation();
  const env = useEnv();
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("hisabx.env.banner.dismissed") === "1"; } catch { return false; }
  });
  if (env.is_preview) {
    if (dismissed) return null;
    return (
      <div
        className="sticky top-0 z-[60] flex items-center justify-center gap-2 border-b-2 border-amber-500 bg-amber-500 px-3 py-1.5 text-[11px] font-bold text-amber-950 shadow-md"
        data-testid="env-preview-banner"
      >
        <span aria-hidden>🧪</span>
        <span>PREVIEW · Safe to break stuff. Data resets on demand.</span>
        <button
          onClick={() => {
            setDismissed(true);
            try { sessionStorage.setItem("hisabx.env.banner.dismissed", "1"); } catch { /* ignore */ }
          }}
          className="ml-2 rounded bg-amber-950/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-950 hover:bg-amber-950/30"
          aria-label={t("components.envbanner.dismiss_preview_banner")}
        >
          hide
        </button>
      </div>
    );
  }
  // Production — silent. Previous version rendered a pulsing red "LIVE" dot
  // in the top-right corner (data-testid=env-prod-marker) which felt like a
  // demo artifact on real dealer screens. Brick 5 N1 kills it entirely.
  return null;
}
